import { MyQuiz } from '@/features/Quiz/components/MyQuiz';
import { SharedQuiz } from '@/features/Quiz/components/SharedQuiz';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
    Navigate,
    NavigateFunction,
    Outlet,
    useLocation,
    useNavigate,
    useRoutes,
    useSearchParams,
} from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { CreateQuiz } from '@/features/Quiz/components/CreateQuiz';
import { CreateVideoLibraryQuiz } from '@/features/Quiz/components/CreateVideoLibraryQuiz';
import { EditQuiz } from '@/features/Quiz/components/EditQuiz';
import { ViewQuiz } from '@/features/Quiz/components/ViewQuiz';
import { Box, Spinner, Tab, TabList, TabPanels, Tabs, useMediaQuery } from '@chakra-ui/react';
import { ArticlesQuiz } from '@/features/Quiz/components/ArticlesQuiz';
import { ArticlesQuizV2 } from '@/features/Quiz/components/ArticlesQuizV2';
import { QuizLibrary } from '@/features/Quiz/components/QuizLibrary';
import { QuizLibraryV2 } from '@/features/Quiz/components/QuizLibraryV2';
import { AdminQuiz } from '@/features/Quiz/components/AdminQuiz';
import { AdminQuizV2 } from '@/features/Quiz/components/AdminQuizV2';
import { SharedQuizV2 } from '@/features/Quiz/components/SharedQuizV2';
import { useAuth } from '@/context/AuthContext';
import { CustomerQuiz } from '@/features/Quiz/components/CustomerQuiz';
import { CustomerQuizV2 } from '@/features/Quiz/components/CustomerQuizV2';
import { CustomerResults } from '@/features/Quiz/components/CustomerResults';
import { CustomerResultsV2 } from '@/features/Quiz/components/CustomerResultsV2';
import { CustomerUserResults } from '@/features/Quiz/components/CustomerUserResults';
import { CustomerAttemptDetailsV2 } from '@/features/Quiz/components/CustomerAttemptDetailsV2';
import { useEducationFeatures } from '@/features/FeatureFlags/api/getFeatureFlagsList';

const quizAdminSharedCustomerV1Routes: RouteObject[] = [
    { path: 'admin-quiz/create', element: <CreateQuiz isAdminQuiz /> },
    { path: 'admin-quiz/copy/:adminQuizId', element: <CreateQuiz isAdminQuiz /> },
    { path: 'admin-quiz/view/:adminQuizId', element: <ViewQuiz /> },
    { path: 'admin-quiz/edit/:adminQuizId', element: <EditQuiz isAdminQuiz /> },
    { path: 'admin-quiz', element: <AdminQuiz /> },

    { path: 'shared-quiz/copy/:adminQuizId', element: <CreateQuiz isAdminQuiz /> },
    { path: 'shared-quiz/view/:adminQuizId', element: <ViewQuiz /> },
    { path: 'shared-quiz/edit/:adminQuizId', element: <EditQuiz isAdminQuiz /> },
    { path: 'shared-quiz', element: <SharedQuiz /> },

    { path: 'customer-quiz', element: <CustomerQuiz /> },
    { path: 'customer-quiz/view/:userQuizId', element: <ViewQuiz /> },
    { path: 'customer-quiz/edit/:userQuizId', element: <EditQuiz isUserQuiz /> },
    { path: 'customer-quiz/copy/:userQuizId', element: <CreateQuiz isUserQuiz /> },
    { path: 'customer-quiz/results/:userQuizId', element: <CustomerResults /> },
    { path: 'customer-quiz/user-results/:assignId', element: <CustomerUserResults /> },
];

const quizAdminSharedCustomerV2Routes: RouteObject[] = [
    { path: 'admin-quiz-v2/create', element: <CreateQuiz isAdminQuiz isAdminQuizV2 /> },
    { path: 'admin-quiz-v2/copy/:quizUuid', element: <CreateQuiz isAdminQuiz isAdminQuizV2 /> },
    { path: 'admin-quiz-v2/edit/:quizUuid', element: <EditQuiz isAdminQuiz isAdminQuizV2 /> },
    { path: 'admin-quiz-v2/view/:quizUuid', element: <ViewQuiz /> },
    { path: 'admin-quiz-v2', element: <AdminQuizV2 /> },

    { path: 'shared-quiz-v2', element: <SharedQuizV2 /> },

    { path: 'customer-quiz-v2/edit/:quizUuid', element: <EditQuiz isCustomerQuizV2 isUserQuiz /> },
    { path: 'customer-quiz-v2/view/:quizUuid', element: <ViewQuiz /> },
    { path: 'customer-quiz-v2/results/:quizUuid', element: <CustomerResultsV2 /> },
    { path: 'customer-quiz-v2/attempt/:attemptUuid', element: <CustomerAttemptDetailsV2 /> },
    { path: 'customer-quiz-v2', element: <CustomerQuizV2 /> },
];

const QuizLibraryRoute = () => {
    const { data: featureData } = useEducationFeatures();
    return featureData?.enableOpenSearchForQuizLibrary ? <QuizLibraryV2 /> : <QuizLibrary />;
};

const ArticlesQuizRoute = () => {
    const { data: featureData } = useEducationFeatures();
    return featureData?.enableOpenSearchForQuizLibrary ? <ArticlesQuizV2 /> : <ArticlesQuiz />;
};

const LibraryCreateQuizRoute = () => {
    const [searchParams] = useSearchParams();
    const { data: featureData, isLoading } = useEducationFeatures();

    if (searchParams.get('libraryType') === 'VIDEO') {
        return <Navigate to="/quiz/library" replace />;
    }

    if (isLoading) return <Spinner />;
    return <CreateQuiz isLibrary libraryQuizV2Api={!!featureData?.enableOpenSearchForQuizLibrary} />;
};

const LibraryEditQuizRoute = () => {
    const { data: featureData, isLoading } = useEducationFeatures();
    if (isLoading) return <Spinner />;
    return <EditQuiz isLibrary libraryQuizV2Api={!!featureData?.enableOpenSearchForQuizLibrary} />;
};

/** Article quiz view/edit/copy: legacy numeric IDs or library V2 UUIDs when flag is on. */
const ArticleCreateQuizRoute = () => {
    const { data: featureData, isLoading } = useEducationFeatures();
    if (isLoading) return <Spinner />;
    return <CreateQuiz libraryQuizV2Api={!!featureData?.enableOpenSearchForQuizLibrary} />;
};

const ArticleEditQuizRoute = () => {
    const { data: featureData, isLoading } = useEducationFeatures();
    if (isLoading) return <Spinner />;
    return <EditQuiz libraryQuizV2Api={!!featureData?.enableOpenSearchForQuizLibrary} />;
};

const quizCommonChildRoutes: RouteObject[] = [
    { path: 'my-quiz/view/:userQuizId', element: <ViewQuiz /> },
    // My Quizzes V2 (quiz-svc) owner actions kept under the my-quiz namespace so the route guard and tab stay correct.
    { path: 'my-quiz/edit/:quizUuid', element: <EditQuiz isMyQuizV2 isUserQuiz /> },
    { path: 'my-quiz/results/:quizUuid', element: <CustomerResultsV2 /> },
    { path: 'my-quiz/attempt/:attemptUuid', element: <CustomerAttemptDetailsV2 /> },
    { path: 'my-quiz/create', element: <CreateQuiz isMyQuizV2 isUserQuiz /> },
    { path: 'my-quiz/copy/:userQuizId', element: <CreateQuiz isUserQuiz /> },
    { path: 'my-quiz', element: <MyQuiz /> },

    { path: 'shared-quiz', element: <SharedQuiz /> },

    { path: 'library/create', element: <LibraryCreateQuizRoute /> },
    { path: 'library/copy/:quizLibraryId', element: <LibraryCreateQuizRoute /> },
    { path: 'library/view/:quizLibraryId', element: <ViewQuiz /> },
    { path: 'library/edit/:quizLibraryId', element: <LibraryEditQuizRoute /> },
    { path: 'library', element: <QuizLibraryRoute /> },

    { path: 'user-quiz/edit/:userQuizId', element: <EditQuiz isUserQuiz /> },
    { path: 'user-quiz/view/:userQuizId', element: <ViewQuiz /> },

    { path: 'create', element: <CreateVideoLibraryQuiz /> },
    { path: 'create/:articleId', element: <CreateQuiz /> },
    { path: 'copy/:quizId', element: <ArticleCreateQuizRoute /> },
    { path: 'view/:id', element: <ViewQuiz /> },
    { path: 'edit/:quizId', element: <ArticleEditQuizRoute /> },
    { path: '/', element: <ArticlesQuizRoute /> },
    { path: '*', element: <Navigate to="/" /> },
];

const buildQuizRoutes = (enableQuizV2Api: boolean): RouteObject[] => [
    {
        path: '/',
        element: <Quizzes isAdminQuizV2={enableQuizV2Api} />,
        children: [
            ...(enableQuizV2Api ? quizAdminSharedCustomerV2Routes : quizAdminSharedCustomerV1Routes),
            ...quizCommonChildRoutes,
        ],
    },
];

export const QuizRoutes = () => {
    const routes = useMemo(() => buildQuizRoutes(true), []);

    const element = useRoutes(routes);

    return <>{element}</>;
};

const SuperAdminQuizTabs = ({
    navigate,
    isAdminQuizV2,
}: {
    navigate: NavigateFunction;
    isAdminQuizV2: boolean;
}) => (
    // Chakra UI: many Tab children exceed TypeScript union depth (TS2589).
    // @ts-expect-error Expression produces a union type that is too complex to represent.
    <TabList>
        <Tab onClick={() => navigate('')}>Articles Quiz Template</Tab>
        <Tab onClick={() => navigate('library')}>Quiz Library</Tab>
        <Tab onClick={() => navigate(isAdminQuizV2 ? 'admin-quiz-v2' : 'admin-quiz')}>
            {isAdminQuizV2 ? 'Admin Quizzes V2' : 'Admin Quizzes'}
        </Tab>
        <Tab onClick={() => navigate(isAdminQuizV2 ? 'shared-quiz-v2' : 'shared-quiz')}>
            {isAdminQuizV2 ? 'Shared By Admins V2' : 'Shared By Admins'}
        </Tab>
        <Tab onClick={() => navigate(isAdminQuizV2 ? 'customer-quiz-v2' : 'customer-quiz')}>
            {isAdminQuizV2 ? 'Customer Quizzes V2' : 'Customer Quizzes'}
        </Tab>
    </TabList>
);

const UserQuizTabs = ({ navigate }: { navigate: NavigateFunction }) => (
    <TabList>
        <Tab onClick={() => navigate('my-quiz')}>My Quizzes</Tab>
        <Tab onClick={() => navigate('shared-quiz')}>Shared By Me</Tab>
    </TabList>
);

const Quizzes = ({
    isAdminQuizV2,
}: {
    /** From `educationFeatures` → `enableQuizV2Api` (institution override, then global). */
    isAdminQuizV2: boolean;
}) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const { user } = useAuth();

    const [tabIndex, setTabIndex] = useState(0);
    const [isSmThan900] = useMediaQuery(['(max-width: 900px)']);

    const isSuperAdmin = user?.roles.includes('ROLE_SUPER_ADMIN');
    const isArticleQuiz = !isSuperAdmin && (pathname === '/quiz' || pathname === '/quiz/');

    useEffect(() => {
        if (!pathname) return setTabIndex(0);

        if (pathname.includes('/quiz/library')) {
            setTabIndex(isSuperAdmin ? 1 : 0);
        } else if (
            (pathname.includes('/quiz/admin-quiz') && !pathname.includes('admin-quiz-v2')) ||
            pathname.includes('/quiz/admin-quiz-v2') ||
            pathname.includes('/quiz/my-quiz')
        ) {
            setTabIndex(isSuperAdmin ? 2 : 0);
        } else if (
            (pathname.includes('/quiz/shared-quiz') && !pathname.includes('shared-quiz-v2')) ||
            pathname.includes('/quiz/shared-quiz-v2')
        ) {
            setTabIndex(isSuperAdmin ? 3 : 1);
        } else if (
            (pathname.includes('/quiz/customer-quiz') && !pathname.includes('customer-quiz-v2')) ||
            pathname.includes('/quiz/customer-quiz-v2')
        ) {
            setTabIndex(isSuperAdmin ? 4 : 1);
        } else return setTabIndex(0);
    }, [pathname, isSuperAdmin]);

    if (isArticleQuiz && pathname.includes('/quiz/library')) {
        return <Navigate to={isAdminQuizV2 ? '/quiz/admin-quiz-v2' : '/quiz/admin-quiz'} />;
    }
    if (!isSuperAdmin && pathname === '/quiz') {
        return <Navigate to="/quiz/my-quiz" />;
    }
    if (!isSuperAdmin && !pathname.includes('my-quiz') && !pathname.includes('shared-quiz')) {
        return <Navigate to="/quiz/my-quiz" />;
    }

    const tabPanelHeight = isSmThan900 ? 'calc(100vh - 195px)' : 'calc(100vh - 165px)';

    let quizTabs: React.ReactNode = null;
    if (!pathname.includes('/quiz/user-quiz')) {
        quizTabs = isSuperAdmin
            ? <SuperAdminQuizTabs navigate={navigate} isAdminQuizV2={isAdminQuizV2} />
            : <UserQuizTabs navigate={navigate} />;
    }

    return (
        <Box>
            <Tabs variant="line" isLazy index={tabIndex}>
                {quizTabs}
                <TabPanels h={tabPanelHeight} px={1} pb={4} pt={6}>
                    <Suspense
                        fallback={
                            <div className="h-full w-full flex items-center justify-center">
                                <Spinner size="xl" />
                            </div>
                        }>
                        <Outlet />
                    </Suspense>
                </TabPanels>
            </Tabs>
        </Box>
    );
};
